import { createRoot } from "react-dom/client";
import { createElement } from "react";
import { SingleEmbed } from "../single/SingleEmbed";
import { PlaylistEmbed } from "../playlist/PlaylistEmbed";
import { ReelsEmbed } from "../reels/ReelsEmbed";
import { parseSingleProps, parsePlaylistProps, parseReelsProps } from "./parse";
import { registerElements } from "./elements";
import type { SingleEmbedProps } from "../single/SingleEmbed";
import type { PlaylistEmbedProps } from "../playlist/PlaylistEmbed";
import type { ReelsEmbedProps } from "../reels/ReelsEmbed";
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://f1c49b9e8a7cad1bd222217e20110fe7@o4505393853956096.ingest.us.sentry.io/4511338887774208",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: false,
});

type EmbedType = "single" | "video" | "playlist" | "reels";
type EmbedProps = SingleEmbedProps | PlaylistEmbedProps | ReelsEmbedProps;

const MOUNTED_ATTR = "data-seeen-mounted";

const components = {
  single: SingleEmbed,
  video: SingleEmbed, // alias for seeen-video / data-seeen="video"
  playlist: PlaylistEmbed,
  reels: ReelsEmbed,
} as const;

function mountElement(el: Element, type: EmbedType, props: EmbedProps) {
  if (el.hasAttribute(MOUNTED_ATTR)) return;
  el.setAttribute(MOUNTED_ATTR, "true");
  const root = createRoot(el, {
    // Error reporting: captures all errors
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    createElement(components[type] as React.ComponentType<EmbedProps>, props),
  );
}

function discoverAndMount() {
  document.querySelectorAll<Element>("[data-seeen]").forEach((el) => {
    if (el.hasAttribute(MOUNTED_ATTR)) return;
    const type = (el as HTMLElement).dataset.seeen as EmbedType;
    if (!components[type]) return;
    try {
      const props =
        type === "single" || type === "video"
          ? parseSingleProps(el)
          : type === "playlist"
            ? parsePlaylistProps(el)
            : parseReelsProps(el);
      mountElement(el, type, props);
    } catch (e) {
      Sentry.captureException(e);
      console.error(`[Seeen] Failed to mount "${type}" embed:`, e);
    }
  });
}

export type MountFn = {
  (type: "single" | "video", target: string | Element, props: SingleEmbedProps): void;
  (type: "playlist", target: string | Element, props: PlaylistEmbedProps): void;
  (type: "reels", target: string | Element, props: ReelsEmbedProps): void;
};

function mount(type: EmbedType, target: string | Element, props: EmbedProps) {
  const el =
    typeof target === "string" ? document.querySelector(target) : target;
  if (!el) {
    console.error(`[Seeen] mount: element not found: "${target}"`);
    return;
  }
  if (!components[type]) {
    console.error(
      `[Seeen] mount: unknown type "${type}". Valid types: single, playlist, reels`,
    );
    return;
  }
  mountElement(el, type, props);
}

// Guard: if another copy of this script already ran, skip re-initialization
// but expose mount() so late callers still work.
declare global {
  interface Window {
    Seeen: {
      mount: MountFn;
      version: string;
    };
  }
}

if (!window.Seeen) {
  window.Seeen = {
    mount: mount as MountFn,
    version: "__VERSION__",
  };

  registerElements();

  // Auto-mount elements present at script-load time
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", discoverAndMount);
  } else {
    discoverAndMount();
  }

  // Watch for elements added after initial load (e.g. CMS lazy blocks)
  const observer = new MutationObserver(discoverAndMount);
  observer.observe(document.body, { childList: true, subtree: true });
}
